// V164.2: API Redirect Interceptor
// Redirects API calls to use NetworkDataContext and direct Supabase calls to avoid 404s
// D001: No direct peer reads on public.users — use peer_users_for_viewer or self eq(auth.uid())

import { supabase } from '../integrations/supabase/client';

let isInterceptorInstalled = false;
let originalFetch: typeof fetch;

// Cache for network data to avoid repeated calls
const networkDataCache = new Map<string, any>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function installApiRedirectInterceptor() {
  if (isInterceptorInstalled) return;
  
  originalFetch = window.fetch;
  
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input.toString();
    
    if (url.includes('/api/')) {
      try {
        const redirectedResponse = await handleApiRedirect(url, init);
        if (redirectedResponse) {
          return redirectedResponse;
        }
      } catch (error) {
        console.warn('🔄 V164.2: API redirect failed, falling back to original:', error);
      }
    }
    
    return originalFetch(input, init);
  };
  
  isInterceptorInstalled = true;
}

export function uninstallApiRedirectInterceptor() {
  if (!isInterceptorInstalled || !originalFetch) return;
  
  window.fetch = originalFetch;
  isInterceptorInstalled = false;
  console.log('🔄 V164.2: API redirect interceptor uninstalled');
}

async function handleApiRedirect(url: string, init?: RequestInit): Promise<Response | null> {
  const userIdMatch = url.match(/\/api\/(?:users|network|connections)\/([^\/\?]+)/);
  const userId = userIdMatch?.[1];
  
  if (url.includes('/api/users/') && userId) {
    return handleUserApiRequest(userId);
  }
  
  if (url.includes('/api/network/') && userId) {
    return handleNetworkApiRequest(userId);
  }
  
  if (url.includes('/api/connections/') && userId) {
    return handleConnectionsApiRequest(userId);
  }
  
  if (url.includes('/api/connections') && !userId) {
    return handleGeneralConnectionsApiRequest();
  }
  
  if (url.includes('/api/matches')) {
    return handleMatchesApiRequest();
  }
  
  if (url.includes('/api/profiles')) {
    return handleProfilesApiRequest();
  }
  
  return null;
}

/** Map peer_users_for_viewer row to legacy /api/users JSON shape (no peer email). */
function mapPeerRowToApiUser(row: Record<string, unknown>) {
  return {
    id: row.id,
    name: row.name,
    email: '',
    photo_urls: row.photo_urls,
    bio: row.bio,
    dob: row.date_of_birth ?? null,
    gender: row.gender ?? null,
    trust_score: row.trust_score,
    dating_preferences: row.dating_preferences,
    created_at: row.created_at,
    updated_at: row.created_at,
  };
}

async function handleUserApiRequest(userId: string): Promise<Response> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return new Response(JSON.stringify({ error: 'Unauthorized' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  if (user.id === userId) {
    const { data: userData, error } = await supabase
      .from('users')
      .select('id, name, email, photo_urls, bio, date_of_birth, gender, trust_score, dating_preferences, created_at, updated_at')
      .eq('id', userId)
      .single();

    if (error) {
      return new Response(JSON.stringify({ error: error.message }), {
        status: 404,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    const payload = {
      ...userData,
      dob: userData?.date_of_birth ?? null,
    };
    return new Response(JSON.stringify(payload), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
  const { data: rows, error } = await rpcPeerUsersForViewer(supabase, [userId], { includePrefs: true });
  if (error) {
    return new Response(JSON.stringify({ error: error.message }), {
      status: 404,
      headers: { 'Content-Type': 'application/json' }
    });
  }
  const row = rows?.[0] as Record<string, unknown> | undefined;
  if (!row?.id) {
    return new Response(JSON.stringify({ error: 'Not found' }), {
      status: 404,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  return new Response(JSON.stringify(mapPeerRowToApiUser(row)), {
    status: 200,
    headers: { 'Content-Type': 'application/json' }
  });
}

async function handleNetworkApiRequest(userId: string): Promise<Response> {
  const cacheKey = `network_${userId}`;
  const cached = networkDataCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return new Response(JSON.stringify(cached.data), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }
  
  try {
    const { getFullConnections } = await import('./connectionCoordinator');
    const connectionsData = await getFullConnections(userId);
    
    const networkData = {
      totalConnections: connectionsData.firstDegree.length + connectionsData.secondDegree.length + connectionsData.thirdDegree.length,
      firstDegreeConnections: connectionsData.firstDegree.length,
      secondDegreeConnections: connectionsData.secondDegree.length,
      thirdDegreeConnections: connectionsData.thirdDegree.length,
      trustScore: 0,
      connections: [...connectionsData.firstDegree, ...connectionsData.secondDegree, ...connectionsData.thirdDegree],
      favorites: 0
    };
    
    networkDataCache.set(cacheKey, {
      data: networkData,
      timestamp: Date.now()
    });
    
    return new Response(JSON.stringify(networkData), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  } catch (error: any) {
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { 'Content-Type': 'application/json' }
    });
  }
}

async function handleConnectionsApiRequest(userId: string): Promise<Response> {
  try {
    const { getFullConnections } = await import('./connectionCoordinator');
    const connectionsData = await getFullConnections(userId);
    
    const connections = [
      ...connectionsData.firstDegree,
      ...connectionsData.secondDegree,
      ...connectionsData.thirdDegree
    ].filter(conn => conn.status === 'ACCEPTED' || conn.status === 'approved');
    
    return new Response(JSON.stringify(connections), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  } catch (error: any) {
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { 'Content-Type': 'application/json' }
    });
  }
}

async function handleGeneralConnectionsApiRequest(): Promise<Response> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return new Response(JSON.stringify({ error: 'Unauthorized' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  const viewerId = user.id;

  const { data: connectionRows, error } = await supabase
    .from('connections')
    .select('id, user_id, connection_user_id, status, type, created_at, updated_at')
    .or(`user_id.eq.${viewerId},connection_user_id.eq.${viewerId}`)
    .eq('status', 'ACCEPTED');

  if (error) {
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  const rows = connectionRows || [];
  const allIds = new Set<string>();
  rows.forEach((c: { user_id: string; connection_user_id: string }) => {
    allIds.add(c.user_id);
    allIds.add(c.connection_user_id);
  });
  const peerIds = [...allIds].filter((id) => id !== viewerId);

  const { data: selfData } = await supabase
    .from('users')
    .select('id, name, photo_urls')
    .eq('id', viewerId)
    .maybeSingle();

  const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
  const { data: peerRows, error: rpcErr } = await rpcPeerUsersForViewer(supabase, peerIds, { includePrefs: false });

  if (rpcErr) {
    return new Response(JSON.stringify({ error: rpcErr.message }), {
      status: 500,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  const idToMini = new Map<string, { id: string; name: string | null; photo_urls: unknown }>();
  if (selfData) {
    idToMini.set(viewerId, {
      id: selfData.id,
      name: selfData.name,
      photo_urls: selfData.photo_urls
    });
  }
  (peerRows || []).forEach((r: { id: string; name: string | null; photo_urls: unknown }) => {
    idToMini.set(r.id, { id: r.id, name: r.name, photo_urls: r.photo_urls });
  });

  const connections = rows.map((c: { user_id: string; connection_user_id: string }) => ({
    ...c,
    user: idToMini.get(c.user_id) ?? { id: c.user_id, name: null, photo_urls: null },
    connection_user: idToMini.get(c.connection_user_id) ?? { id: c.connection_user_id, name: null, photo_urls: null }
  }));

  return new Response(JSON.stringify({ connections }), {
    status: 200,
    headers: { 'Content-Type': 'application/json' }
  });
}

async function handleMatchesApiRequest(): Promise<Response> {
  try {
    const { data: matches, error } = await supabase
      .from('matches')
      .select('*');

    if (error) {
      console.error('V164.4: Matches query error:', error);
      return new Response(JSON.stringify({ error: error.message }), {
        status: 500,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    return new Response(JSON.stringify({ matches: matches || [] }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  } catch (error) {
    console.error('V164.4: Matches API error:', error);
    return new Response(JSON.stringify({ matches: [] }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }
}

async function handleProfilesApiRequest(): Promise<Response> {
  const { data: profiles, error } = await supabase
    .from('profiles')
    .select('*')
    .limit(50);
  
  if (error) {
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { 'Content-Type': 'application/json' }
    });
  }
  
  return new Response(JSON.stringify({ profiles: profiles || [] }), {
    status: 200,
    headers: { 'Content-Type': 'application/json' }
  });
}
